import {Component, computed, inject, input, model, signal} from '@angular/core';
import {CheckboxComponent} from '../../../../../shared/components/checkbox/checkbox.component';
import {IItem} from '../../../../../types/IItem';
import {FiltersSidebarFilterComponent} from '../filters-sidebar-filter/filters-sidebar-filter.component';
import {Search} from '../../../../../shared/components/search/search';
import {LinkComponent} from '../../../../../shared/components/link/link.component';
import {IconsComponent} from '../../../../../shared/components/icons/icons.component';
import {ResultTableService} from '../../../services/result-table.service';
import {ResultsStore} from '../../../../../shared/store/results.store';
import {getSourcesByType} from '../../../../../shared/utils/availability.utils';
import {AccessService} from '../../../../../shared/services/access.service';

@Component({
  selector: 'app-filters-sidebar-filter-checkboxes',
  standalone: true,
  imports: [CheckboxComponent, FiltersSidebarFilterComponent, Search, LinkComponent, IconsComponent],
  templateUrl: './filters-sidebar-filter-checkboxes.component.html',
  styleUrl: './filters-sidebar-filter-checkboxes.component.css'
})
export class FiltersSidebarFilterCheckboxesComponent {
  label = input.required<string>();
  key = input.required<string>();

  search = model<string>('');
  hideLinks = signal(true);

  private resultsStore = inject(ResultsStore);
  private resultsTableService = inject(ResultTableService);
  private access = inject(AccessService);

  checkChanged(option: string, checked: boolean) {
    let options = this.resultsTableService.filterConfig()?.[this.key()] || [];
    options = checked ? [...options, option] : options.filter((b: any) => b !== option);
    this.resultsTableService.updateFilter(this.key(), options);
  }

  isChecked(option: string) {
    return this.resultsTableService.filterConfig()[this.key()]?.includes(option)
  }

  searchable = computed(() => this.extractOptions().length > 7);

  options = computed(() => this.filterBySearch(this.extractOptions()));

  extractOptions = computed(() => {
    const results = this.resultsStore.results();
    if(!results) return [];
    switch (this.key()) {
      case 'delivery':
        return this.extractDeliveryOptions(results);
      case 'specials':
        return this.sortOptions(this.extractSpecialOptions(results));
      case 'features.run_flat':
        return this.extractRunFlatOptions();
      case 'features.speed_rating':
        return this.extractSpeedRatingOptions(results);
      case 'features.load_range':
        return this.extractLoadRangeOptions(results);
      default:
        return this.sortOptions(this.extractStandardOptions(results));
    }
  });

  private extractDeliveryOptions(results: IItem[]): {label: string, value: string}[] {
    let options: string[] = [];

    results.forEach((item: IItem) => {
      getSourcesByType(item.availability, 'distributor').forEach((dist: any) => {
        options.push(`${dist.vendor.name} - ${dist.name}`);
      });

      getSourcesByType(item.availability, 'transfer').forEach((transfer: any) => {
        options.push(transfer.name);
      });
    });

    options = [...new Set(options)].sort();
    options.unshift('On Hand');
    options.push('Not Available');

    return options.map(item => ({label: item, value: item}));
  }

  private extractSpecialOptions(results: IItem[]): {label: string, value: string}[] {
    const checks: [condition: (item: IItem) => unknown, label: string][] = [
      [item => item.rebates?.length, 'Rebate'],
      [item => item.spiff && this.access.has('view_spiff_codes') && this.access.can('view_spiff_codes'), 'Spiff'],
      [item => item.programs?.length, 'Program'],
    ];

    return checks.filter(([condition]) => results.some(condition)).map(([, label]) => label).map(item => ({label: item, value: item}));
  }

  private extractRunFlatOptions() {
    return ['Yes', 'No'].map(item => ({label: item, value: item}));
  }

  private extractSpeedRatingOptions(results: IItem[]) {
    const only = [...new Set(results.map((item: IItem) => item.features.speed_rating))];

    return [
      {label: 'F - 50 mph / 80 km/h', value: 'F'},
      {label: 'G - 56 mph / 90 km/h', value: 'G'},
      {label: 'L - 75 mph / 120 km/h', value: 'L'},
      {label: 'M - 81 mph / 130 km/h', value: 'M'},
      {label: 'N - 87 mph / 140 km/h', value: 'N'},
      {label: 'P - 93 mph / 150 km/h', value: 'P'},
      {label: 'Q - 99 mph / 160 km/h', value: 'Q'},
      {label: 'R - 106 mph / 170 km/h', value: 'R'},
      {label: 'S - 112 mph / 180 km/h', value: 'S'},
      {label: 'T - 118 mph / 190 km/h', value: 'T'},
      {label: 'U - 124 mph / 200 km/h', value: 'U'},
      {label: 'H - 130 mph / 210 km/h', value: 'H'},
      {label: 'V - 149 mph / 240 km/h', value: 'V'},
      {label: 'W - 168 mph / 270 km/h', value: 'W'},
      {label: 'Y - 186 mph / 300 km/h', value: 'Y'},
      {label: 'Z - 149+ mph / 240+ km/h', value: 'Z'},
    ].filter(({value}) => only.includes(value));
  }

  private extractLoadRangeOptions(results: IItem[]) {
    const only = [...new Set(results.map((item: IItem) => item.features.load_range))];

    return [
      {label: 'LL - Light Load', value: 'LL'},
      {label: 'SL (4) - Standard Load', value: 'SL'},
      {label: 'RF (6) - Reinforced', value: 'RF'},
      {label: 'XL (6) - Extra Load', value: 'XL'},
      {label: 'B (4)', value: 'B'},
      {label: 'C (6)', value: 'C'},
      {label: 'C1 (6)', value: 'C1'},
      {label: 'C2 (6)', value: 'C2'},
      {label: 'D (8)', value: 'D'},
      {label: 'E (10)', value: 'E'},
      {label: 'F (12)', value: 'F'},
      {label: 'G (14)', value: 'G'},
      {label: 'H (16)', value: 'H'},
      {label: 'J (18)', value: 'J'},
      {label: 'K', value: 'K'},
      {label: 'L (20)', value: 'L'},
      {label: 'M (22)', value: 'M'},
      {label: 'N (24)', value: 'N'},
    ].filter(({value}) => only.includes(value));
  }

  private extractStandardOptions(results: IItem[]): {label: string, value: string}[] {
    return [...new Set(results.map((item: IItem) => this.getOptionValue(item)?.toString() ?? '').filter((option: string) => option !== ''))].map(item => ({label: item, value: item}));
  }

  private filterBySearch(options: {label: string, value: string}[]): {label: string, value: string}[] {
    if (!this.search()) {
      return options;
    }

    if (this.key() === 'size') {
      return options.filter(option => {
        return option.value.toLowerCase().startsWith(this.search().toLowerCase()) || option.value.replace(/[^0-9]/g, '').startsWith(this.search().replace(/\D/g, ''));
      });
    }

    return options.filter(option => option.value.toLowerCase().startsWith(this.search().toLowerCase()));
  }

  private sortOptions(options: {label: string, value: string}[]): {label: string, value: string}[] {
    return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }

  private getOptionValue(item: IItem) {
    let value: any = item;
    for (const part of this.key().split('.')) {
      value = value?.[part];
    }
    return value;
  }
}
